/**
 * Shared OIDC silent-renewal helpers used by AuthProvider.
 * Keeps JWT refresh and Aegis session vitality separate so valid backend sessions
 * are not destroyed when OIDC renewal succeeds or only tokens need re-binding.
 */
import type { User, UserManager } from 'oidc-client-ts';
import type { SessionValidationResult } from './session-management';
import { decodeAndValidateToken } from './utils';

/** Accept tokens slightly past skew to avoid false negatives from clock drift. */
export const OIDC_TOKEN_CLOCK_SKEW_SECONDS = 30;

export function isUsableOidcUser(user: User | null | undefined): boolean {
  if (!user?.access_token) return false;
  const tokenInfo = decodeAndValidateToken(user.access_token);
  if (tokenInfo.timeUntilExpiry === null) {
    return false;
  }
  return tokenInfo.timeUntilExpiry > -OIDC_TOKEN_CLOCK_SKEW_SECONDS;
}

export type ApplyOidcRenewalDeps = {
  setUser: (user: User) => void;
  /** Kept for call-site compatibility; must not receive the OIDC access token (C01). */
  setAuthToken?: (token: { access_token: string; token_type: string }) => void;
};

/** Persist a renewed OIDC user when the access token is usable. Returns true when applied.
 *
 * SECURITY (pentest C01): do NOT write the OIDC access token into authToken storage.
 * That slot is reserved for the Aegis-signed session access token used on API/WebSocket
 * calls. Overwriting it with the IdP JWT causes immediate 401/WS auth failures after renew.
 * Callers that need the OIDC JWT for POST /v1/sessions must read `user.access_token`.
 */
export function applyOidcRenewal(
  user: User | null | undefined,
  deps: ApplyOidcRenewalDeps
): boolean {
  if (!user || !isUsableOidcUser(user)) return false;
  deps.setUser(user);
  return true;
}

type SilentRenewInFlightRef = { current: Promise<User | null> | null };

/**
 * Deduplicate concurrent signinSilent() calls from tokenExpiringSoon, accessTokenExpired,
 * silentRenewError, and WebSocket INVALID_TOKEN handlers.
 */
export async function signinSilentSingleFlight(
  userManager: UserManager,
  inFlightRef: SilentRenewInFlightRef
): Promise<User | null> {
  if (inFlightRef.current) {
    return inFlightRef.current;
  }

  const promise = userManager.signinSilent();
  inFlightRef.current = promise;

  try {
    return await promise;
  } finally {
    if (inFlightRef.current === promise) {
      inFlightRef.current = null;
    }
  }
}

export function isRefreshTokenDeadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  const lower = message.toLowerCase();
  return lower.includes('invalid_grant') || lower.includes('refresh_token');
}

export type ResolveOidcRenewalFailureOptions = {
  sessionId?: string;
  error?: Error;
  validateSession: (sessionId: string) => Promise<SessionValidationResult>;
  clearAuthForReLogin: (
    optionalError?: Error,
    options?: { preventRestoration?: boolean }
  ) => void;
};

/**
 * After OIDC renewal fails, decide whether the Aegis session can continue.
 * Never calls triggerFullSignOut — full IdP sign-out is reserved for explicit logout / failsafe paths.
 *
 * SECURITY (pentest C01): OIDC silent renew (prompt=none) often fails with login_required even
 * while the Aegis session access token is still valid. Clearing the client in that case races
 * createSession on login and boots the user with a false "session expired" message.
 */
export async function resolveOidcRenewalFailure(
  options: ResolveOidcRenewalFailureOptions
): Promise<void> {
  const { sessionId, error, validateSession, clearAuthForReLogin } = options;
  const failureError =
    error ?? new Error('Your session has expired. Please log in again to continue.');

  if (!sessionId) {
    // No Aegis session yet (common during login while silent renew races createSession).
    // Leaving storage alone lets the in-flight mint complete.
    return;
  }

  const result = await validateSession(sessionId);

  if (result.isValid) {
    // IdP renew failed; Aegis session is still good — keep the user signed in.
    return;
  }

  if (result.reason === 'expired' || result.reason === 'revoked') {
    clearAuthForReLogin(
      new Error(result.message || failureError.message),
      { preventRestoration: true }
    );
    return;
  }

  clearAuthForReLogin(failureError);
}

/** Try silent renew and apply JWT when the returned access token is usable. */
export async function trySilentRenewAndApply(
  userManager: UserManager,
  inFlightRef: SilentRenewInFlightRef,
  deps: ApplyOidcRenewalDeps
): Promise<User | null> {
  try {
    const renewedUser = await signinSilentSingleFlight(userManager, inFlightRef);
    if (applyOidcRenewal(renewedUser, deps)) {
      return renewedUser;
    }
    return null;
  } catch {
    return null;
  }
}
